<template>
  <div class="reg-view">
    <div v-if="sent" class="reg-card reg-card--center">
      <span class="mdi mdi-email-check-outline reg-card__icon"></span>
      <h1 class="reg-card__title">Revisa tu correo</h1>
      <p class="reg-card__text">
        Si <strong>{{ form.email }}</strong> es un correo nuevo en Parkos, te enviamos un enlace para confirmarlo y crear tu contraseña.
        El enlace vence en 72 horas.
      </p>
      <p class="reg-card__hint">¿No llega? Revisa la carpeta de spam. Si ya tienes cuenta con ese correo, <router-link to="/login">inicia sesión</router-link>.</p>
      <button type="button" class="reg-card__link" @click="reset">Usar otro correo</button>
    </div>

    <form v-else class="reg-card" novalidate @submit.prevent="onSubmit">
      <div class="reg-card__logo">
        <ParkosMark :size="30" />
        <span class="reg-card__logo-text">Parkos</span>
      </div>
      <h1 class="reg-card__title">Crea tu cuenta</h1>
      <p class="reg-card__subtitle">Prueba Parkos 14 días. No necesitas tarjeta.</p>

      <label class="reg-card__label" for="reg-name">Tu nombre</label>
      <input id="reg-name" v-model.trim="form.name" type="text" class="reg-card__input" autocomplete="name" maxlength="100" required />

      <label class="reg-card__label" for="reg-email">Correo de trabajo</label>
      <input id="reg-email" v-model.trim="form.email" type="email" class="reg-card__input" autocomplete="email" maxlength="160" required />

      <label class="reg-card__label" for="reg-park">Nombre del parque</label>
      <input id="reg-park" v-model.trim="form.parkName" type="text" class="reg-card__input" autocomplete="organization" maxlength="120" required />

      <!-- Campo trampa para bots: las personas no lo ven. -->
      <input v-model="form.website" type="text" class="reg-card__hp" tabindex="-1" autocomplete="off" aria-hidden="true" />

      <p v-if="errorMessage" class="reg-card__error" role="alert">{{ errorMessage }}</p>

      <button type="submit" class="reg-card__submit" :disabled="submitting">
        <span v-if="submitting" class="mdi mdi-loading reg-card__spinner"></span>
        <span>{{ submitting ? 'Enviando...' : 'Crear cuenta' }}</span>
      </button>

      <p class="reg-card__legal">
        Al continuar aceptas los
        <a href="https://parkosindustrial.com/terminos.html" target="_blank" rel="noopener">términos de uso</a> y el
        <a href="https://parkosindustrial.com/privacidad.html" target="_blank" rel="noopener">aviso de privacidad</a>.
      </p>
      <router-link class="reg-card__link" to="/login">¿Ya tienes cuenta? Inicia sesión</router-link>
    </form>
  </div>
</template>

<script setup>
import ParkosMark from '@/components/Global/ParkosMark.vue'
import { ref, reactive } from 'vue'
import { signup } from '@/services/auth'

const form = reactive({ name: '', email: '', parkName: '', website: '' })
const submitting = ref(false)
const errorMessage = ref('')
const sent = ref(false)

const ERRORS = {
  INVALID_NAME: 'Escribe tu nombre.',
  INVALID_PARK_NAME: 'Escribe el nombre de tu parque.',
  INVALID_EMAIL: 'Revisa que el correo sea válido.',
  TOO_MANY_REQUESTS: 'Hiciste demasiados intentos. Espera un rato e inténtalo de nuevo.',
}

async function onSubmit() {
  errorMessage.value = ''
  if (!form.name || !form.parkName || !form.email) {
    errorMessage.value = 'Completa todos los campos.'
    return
  }
  submitting.value = true
  try {
    await signup({ name: form.name, email: form.email, parkName: form.parkName, website: form.website })
    sent.value = true
  } catch (err) {
    errorMessage.value = ERRORS[err.message] || 'No pudimos crear tu cuenta. Inténtalo de nuevo en unos minutos.'
  } finally {
    submitting.value = false
  }
}

function reset() {
  sent.value = false
  form.email = ''
}
</script>

<style scoped>
.reg-view { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #e6eaf3; padding: 1rem; }
.reg-card { width: 100%; max-width: 400px; background: #fff; border-radius: 12px; box-shadow: 0 10px 30px rgba(30, 58, 95, 0.12); padding: 2rem 1.75rem; display: flex; flex-direction: column; }
.reg-card--center { align-items: center; text-align: center; gap: 0.5rem; }
.reg-card__icon { font-size: 40px; color: #3b82f6; }
.reg-card__logo { display: flex; align-items: center; gap: 0.5rem; color: #1e3a5f; font-size: 1.2rem; margin-bottom: 1rem; }
.reg-card__logo-text { font-family: var(--font-display); font-weight: 700; }
.reg-card__title { font-family: var(--font-display); font-size: 1.3rem; color: #1e3a5f; margin: 0 0 0.25rem; }
.reg-card__subtitle { font-family: var(--font-body); font-size: 0.85rem; color: #64748b; margin: 0 0 1.5rem; }
.reg-card__text { font-family: var(--font-body); font-size: 0.9rem; color: #334155; margin: 0.25rem 0; line-height: 1.5; }
.reg-card__hint { font-family: var(--font-body); font-size: 0.8rem; color: #64748b; margin: 0.5rem 0 0.75rem; line-height: 1.5; }
.reg-card__hint a, .reg-card__legal a { color: #3b82f6; }
.reg-card__label { font-family: var(--font-body); font-size: 0.8rem; color: #64748b; margin-bottom: 0.3rem; }
.reg-card__input { font-family: var(--font-body); font-size: 0.9rem; color: #1e3a5f; border: 1px solid var(--border-active); border-radius: 8px; padding: 0.6rem 0.75rem; margin-bottom: 1rem; }
.reg-card__input:focus { outline: none; border-color: #3b82f6; }
.reg-card__hp { position: absolute; left: -9999px; opacity: 0; height: 0; width: 0; }
.reg-card__error { font-family: var(--font-body); font-size: 0.8rem; color: #e84545; margin: -0.5rem 0 1rem; }
.reg-card__submit { display: flex; align-items: center; justify-content: center; gap: 0.5rem; height: 40px; border: none; border-radius: 8px; background: #3b82f6; color: #fff; font-family: var(--font-body); font-size: 0.9rem; font-weight: 500; cursor: pointer; }
.reg-card__submit:disabled { opacity: 0.7; cursor: default; }
.reg-card__spinner { animation: reg-spin 1s linear infinite; }
@keyframes reg-spin { to { transform: rotate(360deg); } }
.reg-card__legal { font-family: var(--font-body); font-size: 0.72rem; color: #94a3b8; line-height: 1.5; margin: 0.9rem 0 0; text-align: center; }
.reg-card__link { margin-top: 0.9rem; background: none; border: none; text-align: center; font-family: var(--font-body); font-size: 0.8rem; color: #3b82f6; text-decoration: none; cursor: pointer; }
.reg-card__link:hover { text-decoration: underline; }
</style>